import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, onSessionLost } from '@/lib/api';
import type { Me, PublicAuthConfig } from '@/lib/types';

/**
 * FE-0-4 / §5.3 — the app bootstraps and routes from a single `GET /auth/me`.
 *
 * Role lives only here, resolved from the authenticated account. Nothing in the UI
 * ever *chooses* a role (§2): the mockup's Employee/Admin toggle is a demo device
 * and must not ship.
 */

interface AuthState {
  me: Me | null;
  /** True until the initial /auth/me settles — routes must not decide before then. */
  loading: boolean;
  /** Pre-auth config for the login page (which sign-in methods to show). */
  publicConfig: PublicAuthConfig | null;
  signIn: (email: string, password: string) => Promise<Me>;
  signInWithMicrosoft: (code: string, state: string) => Promise<Me>;
  signOut: () => Promise<void>;
  /** Adopts a session created by the invite-accept / reset flows. */
  adopt: (me: Me) => void;
  refresh: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [publicConfig, setPublicConfig] = useState<PublicAuthConfig | null>(null);

  const load = useCallback(async () => {
    try {
      setMe(await api<Me>('/auth/me'));
    } catch (cause) {
      // A 401 here is the normal "not signed in yet" case, not a failure.
      if (!(cause instanceof ApiError) || !cause.isAuthError) {
        // eslint-disable-next-line no-console
        console.error('Failed to load session', cause);
      }
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    // Fetched unconditionally: the login page needs it, and it is cheap and public.
    api<PublicAuthConfig>('/auth/config')
      .then(setPublicConfig)
      .catch(() => setPublicConfig({ microsoftSsoEnabled: false, passwordLoginEnabled: true }));
  }, [load]);

  // When a refresh finally fails, drop the session so guards redirect.
  useEffect(() => onSessionLost(() => setMe(null)), []);

  const signIn = useCallback(async (email: string, password: string) => {
    const next = await api<Me>('/auth/login', { method: 'POST', body: { email, password } });
    setMe(next);
    return next;
  }, []);

  const signInWithMicrosoft = useCallback(async (code: string, state: string) => {
    const next = await api<Me>('/auth/microsoft/callback', {
      method: 'POST',
      body: { code, state },
    });
    setMe(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      // Clear locally even if the call failed — the user asked to leave.
      setMe(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      publicConfig,
      signIn,
      signInWithMicrosoft,
      signOut,
      adopt: setMe,
      refresh: load,
      isAdmin: me?.user.role === 'PROGRAM_ADMIN',
    }),
    [me, loading, publicConfig, signIn, signInWithMicrosoft, signOut, load],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
